// Shared types + grouping for the interactive event tooltip used by both the Progress Monitor and
// the Dashboard timelines. The tooltip lists a marker's files/comments/notes, grouped into sections
// appropriate to how much location context the row already carries.

// How much location a row implies, which decides how deep the tooltip groups its events:
//   • 'modules' → group by task, then subtask
//   • 'phases'  → group by subtask
//   • 'steps'   → one flat section (the row is already a single subtask)
export type TipDepth = 'modules' | 'phases' | 'steps';

// One event line in the tooltip. File events carry a fileId so they can download.
export interface TooltipEvent {
    type: string;
    label: string;
    // CommentType enum name (e.g. 'Request', 'Response') for Comment events; picks the tooltip icon.
    commentType: string | null;
    fileId: string | null;
    fileName: string | null;
    phaseName: string;
    stepName: string;
    userName: string;
    // Where this event lives, so the tooltip can offer an "open" link to its task/subtask. Absent for
    // grant-level notes (no phase) - those get no link.
    grantId?: string | null;
    moduleTypeId?: number | null;
    phaseId?: string | null;
    stepId?: string | null;
    // Resubmission round for file events: null = original submission, 1/2/... = resubmission round.
    resubmission: number | null;
    // Whether this event's phase has ANY resubmission (computed across all the grant's events, not
    // just this tooltip). Files in such a phase are tagged "Original Submission" / "Resubmission N";
    // when a phase has no resubmissions at all, nothing is tagged.
    phaseHasResubmissions: boolean;
}

// One date's worth of events. A single marker has one of these; a collapsed cluster has one per day.
export interface HoverTipDay {
    dateStr: string;
    events: TooltipEvent[];
}

// State for the floating hover tooltip. We carry the hovered marker's on-screen box (viewport
// coords) so the panel can anchor just under it and flip above when it would run off the bottom.
export interface HoverTip {
    anchorLeft: number;
    anchorRight: number;
    anchorTop: number;
    anchorBottom: number;
    header: string;
    depth: TipDepth;
    days: HoverTipDay[];
    // Days beyond the cap (clusters only) — surfaced as a "…and N more day(s)" footer.
    extraDays: number;
}

// A collapsed cluster holds one tick per (phase, day), so several phases active on the same date show
// up as separate ticks with the same date string. Merge them into one HoverTipDay per calendar date
// (events concatenated) so the tooltip shows a single "Events on <date>" header, newest date first —
// the phase/step grouping inside the day then separates the phases.
export const mergeTicksToDays = (
    ticks: { ptDateStr: string; ptEvents: TooltipEvent[]; tipDate?: number }[],
): HoverTipDay[] => {
    const sorted = [...ticks].sort((a, b) => (b.tipDate ?? 0) - (a.tipDate ?? 0));
    const byDate = new Map<string, HoverTipDay>();
    const days: HoverTipDay[] = [];
    sorted.forEach(t => {
        let day = byDate.get(t.ptDateStr);
        if (!day) { day = { dateStr: t.ptDateStr, events: [] }; byDate.set(t.ptDateStr, day); days.push(day); }
        day.events.push(...t.ptEvents);
    });
    return days;
};

interface TipGroupable {
    phaseName: string;
    stepName: string;
}

// Orders a row's events into sections appropriate to the current depth so the tooltip reads
// hierarchically (path holds the section's heading(s), outermost first):
//   • steps   → one flat section, path = []
//   • phases  → grouped by subtask,            path = [subtask]
//   • modules → grouped by task, then subtask, path = [task, subtask]
export const groupTipEvents = <T extends TipGroupable>(
    events: T[],
    depth: TipDepth,
): { path: string[]; events: T[] }[] => {
    if (depth === 'steps') return events.length ? [{ path: [], events }] : [];
    const pathOf = (e: T): string[] =>
        depth === 'modules' ? [e.phaseName || '(task)', e.stepName || '(task-level)'] : [e.stepName || '(task-level)'];
    const sections: { path: string[]; events: T[] }[] = [];
    const byKey = new Map<string, { path: string[]; events: T[] }>();
    events.forEach(e => {
        const path = pathOf(e);
        const key = path.join('¦');
        let sec = byKey.get(key);
        if (!sec) { sec = { path, events: [] }; byKey.set(key, sec); sections.push(sec); }
        sec.events.push(e);
    });
    return sections;
};
